import React, { useEffect, Children } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';
export function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Privacy Policy | IntelliEats';
  }, []);
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} className="pt-32 pb-24 bg-white min-h-screen" data-id="element-1066">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1067">
        <header className="mb-12" data-id="element-1068">
          <Link to="/" className="inline-flex items-center text-sm font-medium text-brand-600 hover:text-brand-700 mb-8 transition-colors" data-id="element-1069">
            <ArrowLeft className="w-4 h-4 mr-2" data-id="element-1070" />
            Back to Home
          </Link>
          <div className="flex items-center gap-4 mb-6" data-id="element-1071">
            <div className="bg-brand-100 text-brand-600 p-3 rounded-2xl" data-id="element-1072">
              <Shield className="w-8 h-8" data-id="element-1073" />
            </div>
            <h1 className="text-4xl sm:text-5xl font-black text-slate-900 tracking-tight" data-id="element-1074">
              Privacy Policy
            </h1>
          </div>
          <p className="text-slate-500 text-sm" data-id="element-1075">Last updated: April 7, 2025</p>
        </header>

        <div className="space-y-10 text-slate-700 leading-relaxed" data-id="element-1076">
          <section data-id="element-1077">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-1078">
              1. Introduction
            </h2>
            <p data-id="element-1079">
              Welcome to IntelliEats ("we," "our," or "us"). We are committed to
              protecting your personal information and your right to privacy.
              This Privacy Policy explains how we collect, use, disclose, and
              safeguard your information when you use our mobile application
              IntelliEats (the "App").
            </p>
          </section>

          <section data-id="element-1080">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-1081">
              2. Information We Collect
            </h2>
            <div className="space-y-4" data-id="element-1082">
              <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100" data-id="element-1083">
                <h3 className="font-bold text-slate-900 mb-2" data-id="element-1084">
                  Camera & Photo Access
                </h3>
                <p className="m-0" data-id="element-1085">
                  We access your device camera and photo gallery solely to scan
                  ingredient labels. Images are processed locally on your device
                  for analysis and are not stored
                  permanently.
                </p>
              </div>
              <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100" data-id="element-1086">
                <h3 className="font-bold text-slate-900 mb-2" data-id="element-1087">Usage Data</h3>
                <p className="m-0" data-id="element-1088">
                  We may collect anonymous usage data such as app interactions,
                  scan frequency, and feature usage to improve the App's
                  performance and user experience.
                </p>
              </div>
              <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100" data-id="element-1089">
                <h3 className="font-bold text-slate-900 mb-2" data-id="element-1090">
                  Device Information
                </h3>
                <p className="m-0" data-id="element-1091">
                  We may collect device type, operating system version, and
                  unique device identifiers for analytics and troubleshooting
                  purposes.
                </p>
              </div>
            </div>
          </section>

          <section data-id="element-1092">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-1093">
              3. How We Use Your Information
            </h2>
            <ul className="space-y-3 list-none p-0" data-id="element-1094">
              <li className="flex items-start gap-3" data-id="element-1095">
                <span className="w-2 h-2 bg-brand-500 rounded-full mt-2 shrink-0" data-id="element-1096"></span>
                <span data-id="element-1097">
                  To provide and maintain the ingredient scanning and analysis
                  service
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-1098">
                <span className="w-2 h-2 bg-brand-500 rounded-full mt-2 shrink-0" data-id="element-1099"></span>
                <span data-id="element-1100">
                  To improve, personalize, and expand our App's features
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-1101">
                <span className="w-2 h-2 bg-brand-500 rounded-full mt-2 shrink-0" data-id="element-1102"></span>
                <span data-id="element-1103">To understand and analyze how you use our App</span>
              </li>
              <li className="flex items-start gap-3" data-id="element-1104">
                <span className="w-2 h-2 bg-brand-500 rounded-full mt-2 shrink-0" data-id="element-1105"></span>
                <span data-id="element-1106">
                  To communicate with you for customer service and updates
                </span>
              </li>
            </ul>
          </section>

          <section data-id="element-1107">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-1108">
              4. Data Sharing & Third Parties
            </h2>
            <p data-id="element-1109">
              We do not sell, trade, or rent your personal information to third
              parties. We may share anonymized, aggregated data with analytics
              partners to improve our services. Any third-party services used
              (such as analytics tools) are bound by their own privacy policies.
            </p>
          </section>

          <section data-id="element-1110">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-1111">
              5. Data Security
            </h2>
            <p data-id="element-1112">
              We implement appropriate technical and organizational security
              measures to protect your personal information. However, no method
              of transmission over the Internet or electronic storage is 100%
              secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section data-id="element-1113">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-1114">
              6. Children's Privacy
            </h2>
            <p data-id="element-1115">
              Our App is rated for ages 4+ and does not knowingly collect
              personal information from children under 13. If you believe we
              have collected information from a child under 13, please contact
              us immediately.
            </p>
          </section>

          <section data-id="element-1116">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-1117">
              7. Changes to This Policy
            </h2>
            <p data-id="element-1118">
              We may update this Privacy Policy from time to time. We will
              notify you of any changes by posting the new Privacy Policy on
              this page and updating the "Last updated" date.
            </p>
          </section>

          <section className="bg-brand-50 rounded-2xl p-8 border border-brand-100" data-id="element-1119">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-1120">
              8. Contact Us
            </h2>
            <p className="mb-4" data-id="element-1121">
              If you have any questions about this Privacy Policy, please
              contact us:
            </p>
            <p className="font-medium text-slate-900" data-id="element-1122">
              Email:{' '}
              <a href="mailto:intellieats@gmail.com" className="text-brand-600 hover:text-brand-700 transition-colors" data-id="element-1123">
                intellieats@gmail.com
              </a>
            </p>
            <p className="mt-2" data-id="element-1124">
              Or use our{' '}
              <Link to="/contact" className="text-brand-600 hover:text-brand-700 font-medium transition-colors" data-id="element-1125">
                Contact Support
              </Link>{' '}
              page.
            </p>
          </section>
        </div>
      </div>
    </motion.div>;
}